import { useCallback, useRef, useState } from "react";
import type {
  Page,
  ReportItem,
} from "@/components/common/report-builder/constants";

/**
 * Document state for history tracking.
 * Excludes UI state (selection, editing mode, current page) - only tracks document content.
 */
export interface DocumentState {
  pages: Page[];
  pagesOrder: string[];
  headerItems: ReportItem[];
  footerItems: ReportItem[];
  orientation: "portrait" | "landscape";
}

/**
 * History manager interface with past/present/future pattern.
 * This pattern is used by enterprise tools like Figma, Canva, and professional IDEs.
 */
export interface HistoryManager<T> {
  /** Current document state */
  present: T;

  /** Stack of past states (most recent at end) */
  past: T[];

  /** Stack of future states for redo (most recent at start) */
  future: T[];

  /**
   * Push current state to history before making a change.
   * Call this BEFORE mutating the document.
   * @param state - The current state to save (will become past after mutation)
   */
  pushState: (state: T) => void;

  /**
   * Undo: Move present to future, pop past to present.
   * @returns The previous state to apply, or null if nothing to undo
   */
  undo: () => T | null;

  /**
   * Redo: Move present to past, pop future to present.
   * @returns The next state to apply, or null if nothing to redo
   */
  redo: () => T | null;

  /**
   * Start a batch operation (e.g., drag/resize).
   * Multiple pushState calls during batch are grouped into one undo step.
   */
  startBatch: () => void;

  /**
   * End a batch operation.
   * Creates a single history entry for all changes during the batch.
   */
  endBatch: () => void;

  /** Whether currently in a batch operation */
  isBatching: boolean;

  /** Whether undo is available */
  canUndo: boolean;

  /** Whether redo is available */
  canRedo: boolean;

  /**
   * Clear all history (past and future).
   * Used when loading a new document.
   */
  clear: () => void;

  /**
   * Replace present state without creating history entry.
   * Used for applying undo/redo states.
   */
  replace: (state: T) => void;

  /**
   * Get the current history length (past + future + 1 for present).
   */
  historyLength: number;
}

const MAX_HISTORY_SIZE = 100;

/**
 * Deep clone utility for state snapshots.
 * More efficient than JSON.parse(JSON.stringify()) for known structures.
 */
function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (Array.isArray(obj)) return obj.map((item) => deepClone(item)) as T;

  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
}

/**
 * Enterprise-grade history management hook using immutable past/present/future pattern.
 *
 * Features:
 * - Deterministic state machine (no race conditions)
 * - Action grouping for continuous operations (drag/resize)
 * - Explicit history saves (no effect-based watching)
 * - Configurable max history size
 *
 * Usage:
 * ```tsx
 * const history = useHistoryManager(initialState);
 *
 * // Before mutation:
 * history.pushState(currentState);
 * // Then perform mutation...
 *
 * // For drag operations:
 * onDragStart={() => history.startBatch()}
 * onDragEnd={() => history.endBatch()}
 *
 * // Undo/Redo:
 * const prevState = history.undo();
 * if (prevState) applyState(prevState);
 * ```
 */
export function useHistoryManager<T>(
  initialState: T
): HistoryManager<T> {
  // Core state: past stack, present, future stack
  const [past, setPast] = useState<T[]>([]);
  const [present, setPresent] = useState<T>(initialState);
  const [future, setFuture] = useState<T[]>([]);

  // Batching refs (use refs to avoid stale closures)
  const isBatchingRef = useRef(false);
  const batchStartStateRef = useRef<T | null>(null);
  const batchHasChangesRef = useRef(false);

  // Force re-render trigger for isBatching state
  const [, forceUpdate] = useState({});

  /**
   * Push state to history.
   * If batching, captures the start state on first push.
   * Otherwise, immediately adds to past stack.
   */
  const pushState = useCallback((state: T) => {
    if (isBatchingRef.current) {
      // During batch: capture start state on first change
      if (batchStartStateRef.current === null) {
        batchStartStateRef.current = deepClone(state);
      }
      batchHasChangesRef.current = true;
      // Update present for tracking, but don't push to past yet
      setPresent(deepClone(state));
      return;
    }

    // Normal operation: push to past, clear future
    // NOTE: Do NOT set present here - present should be updated by replace() after mutation
    setPast((prevPast) => {
      const newPast = [...prevPast, deepClone(state)];
      // Limit history size
      if (newPast.length > MAX_HISTORY_SIZE) {
        return newPast.slice(newPast.length - MAX_HISTORY_SIZE);
      }
      return newPast;
    });
    // Don't update present here - it will be updated by replace() after the mutation
    setFuture([]); // Clear redo stack on new action
  }, [past.length, future.length]);

  /**
   * Start a batch operation.
   * All pushState calls until endBatch are grouped into one undo step.
   */
  const startBatch = useCallback(() => {
    if (isBatchingRef.current) return; // Already batching

    isBatchingRef.current = true;
    batchStartStateRef.current = null;
    batchHasChangesRef.current = false;
    forceUpdate({});
  }, []);

  /**
   * End a batch operation.
   * If changes occurred, creates one history entry for the entire batch.
   */
  const endBatch = useCallback(() => {
    if (!isBatchingRef.current) return; // Not batching

    isBatchingRef.current = false;
    const batchStartSnapshot = batchStartStateRef.current;
    const hadBatchChanges = batchHasChangesRef.current;

    // If we have a start state and changes occurred, push start state to past
    if (hadBatchChanges && batchStartSnapshot !== null) {
      setPast((prevPast) => {
        const newPast = [...prevPast, deepClone(batchStartSnapshot)];
        if (newPast.length > MAX_HISTORY_SIZE) {
          return newPast.slice(newPast.length - MAX_HISTORY_SIZE);
        }
        return newPast;
      });
      setFuture([]); // Clear redo stack
    }

    batchStartStateRef.current = null;
    batchHasChangesRef.current = false;
    forceUpdate({});
  }, []);

  /**
   * Undo: Pop from past, push present to future.
   */
  const undo = useCallback((): T | null => {
    let effectivePast = past;

    // Finalize any active batch before undoing.
    // We do this synchronously to avoid races with async state updates.
    if (isBatchingRef.current) {
      isBatchingRef.current = false;
      if (batchHasChangesRef.current && batchStartStateRef.current !== null) {
        effectivePast = [...past, deepClone(batchStartStateRef.current)];
        if (effectivePast.length > MAX_HISTORY_SIZE) {
          effectivePast = effectivePast.slice(
            effectivePast.length - MAX_HISTORY_SIZE
          );
        }
      }
      batchStartStateRef.current = null;
      batchHasChangesRef.current = false;
      forceUpdate({});
    }

    if (effectivePast.length === 0) return null;

    const newPast = [...effectivePast];
    const previousState = newPast.pop()!;

    setPast(newPast);
    setFuture((prevFuture) => [deepClone(present), ...prevFuture]);
    setPresent(previousState);

    return previousState;
  }, [past, present]);

  /**
   * Redo: Pop from future, push present to past.
   */
  const redo = useCallback((): T | null => {
    let effectivePast = past;

    // Finalize any active batch before redoing.
    // This keeps redo deterministic if a batch is still open.
    if (isBatchingRef.current) {
      isBatchingRef.current = false;
      if (batchHasChangesRef.current && batchStartStateRef.current !== null) {
        effectivePast = [...past, deepClone(batchStartStateRef.current)];
        if (effectivePast.length > MAX_HISTORY_SIZE) {
          effectivePast = effectivePast.slice(
            effectivePast.length - MAX_HISTORY_SIZE
          );
        }
      }
      batchStartStateRef.current = null;
      batchHasChangesRef.current = false;
      forceUpdate({});
    }

    if (future.length === 0) return null;

    const newFuture = [...future];
    const nextState = newFuture.shift()!;

    setFuture(newFuture);
    setPast([...effectivePast, deepClone(present)]);
    setPresent(nextState);

    return nextState;
  }, [future, present, past]);

  /**
   * Clear all history.
   */
  const clear = useCallback(() => {
    setPast([]);
    setFuture([]);
    batchStartStateRef.current = null;
    batchHasChangesRef.current = false;
    isBatchingRef.current = false;
    forceUpdate({});
  }, []);

  /**
   * Replace present without creating history.
   * Used when applying undo/redo states or loading documents.
   */
  const replace = useCallback((state: T) => {
    setPresent(deepClone(state));
  }, []);

  return {
    present,
    past,
    future,
    pushState,
    undo,
    redo,
    startBatch,
    endBatch,
    isBatching: isBatchingRef.current,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    clear,
    replace,
    historyLength: past.length + future.length + 1,
  };
}

export default useHistoryManager;
